import type { PageSummary } from '@/api/client';
import { defaultLang } from '@/app/routes';
import { useT } from '@/i18n/LocaleProvider';

export type PageListSidebarProps = {
  activeSlug?: string;
  homeSlug?: string;
  lang?: string;
  isLoading?: boolean;
  isSearching?: boolean;
  pages: PageSummary[];
  query: string;
  searchError?: string | null;
  onAllPages?: () => void;
  onNavigate: (slug: string) => void;
};

export function PageListSidebar({
  activeSlug,
  homeSlug,
  lang,
  isLoading = false,
  isSearching = false,
  pages,
  query,
  searchError,
  onAllPages,
  onNavigate,
}: PageListSidebarProps) {
  const t = useT();
  const isSearchMode = Boolean(query.trim());

  return (
    <aside className="sidebar" aria-label={t('sidebar_aria_label')}>
      <nav id="page-list" className="page-list" aria-label={t('sidebar_nav_label')}>
        {pages.map((page) => (
          <a
            aria-current={page.slug === activeSlug ? 'page' : undefined}
            className={`page-link${page.slug === activeSlug ? ' is-active' : ''}`}
            href={pageHref(page.slug, homeSlug, lang)}
            key={page.id || page.slug}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(page.slug);
            }}
          >
            <strong>{page.title}</strong>
            <span>{page.is_stub ? t('page_index_stub') : page.media_urls.length}</span>
          </a>
        ))}
      </nav>

      {!isLoading && pages.length === 0 ? (
        <div className="page-list-empty">{t('search_empty')}</div>
      ) : null}

      <div className="sidebar-meta">
        <span>
          {pages.length === 1
            ? t('search_pages_one')
            : t('search_pages_many', { count: String(pages.length) })}
        </span>
        {onAllPages && !isSearchMode ? (
          <a
            href="/pages"
            onClick={(event) => {
              event.preventDefault();
              onAllPages();
            }}
          >
            {t('nav_all_pages')}
          </a>
        ) : null}
        {isLoading ? <span>{t('search_loading')}</span> : null}
        {isSearching ? <span>{t('search_searching')}</span> : null}
        {searchError ? <span className="sidebar-error">{searchError}</span> : null}
      </div>
    </aside>
  );
}

function pageHref(slug: string, homeSlug?: string, lang?: string): string {
  const prefix = lang ?? defaultLang;

  if (slug === homeSlug) {
    return `/${prefix}/`;
  }

  return `/${prefix}/pages/${encodeURIComponent(slug)}`;
}
